import styled from '@emotion/styled';
import { HTTPFetchError, RacemapAPIClient } from '@racemap/utilities/api-client';
import { RacemapColors } from '@racemap/utilities/consts/common';
import type { EventTypes } from '@racemap/utilities/consts/events';
import { getRaceResultImportUrl } from '@racemap/utilities/functions/raceResult';
import { isNotEmptyString, isStageEvent } from '@racemap/utilities/functions/utils';
import type { RacemapEvent, RaceResultImportCheckResult } from '@racemap/utilities/types/events';
import type { Immutable } from 'immer';
import { DateTime } from 'luxon';
import { type FC, useEffect, useState } from 'react';
import type { CurrentEvent } from '../../../store/events/events_reducers';
import type { Entry } from '../../BasicComponents/SwitchList';

export const DAYS_IMPORT_STILL_RUN = 3;

// A stage has no Import/Export tab; its participants are imported on the stage group.
export const participantImportPath = (event: {
  id: string;
  type: EventTypes;
  parent: string | null;
}): string => {
  const ownerId = isStageEvent(event) ? event.parent : event.id;
  return `/admin/events/${ownerId}/participants/import_export`;
};

export const eventIsOver = (event: Immutable<RacemapEvent | CurrentEvent>): boolean => {
  return (
    event.endTime == null ||
    DateTime.fromISO(event.endTime) < DateTime.utc().minus({ days: DAYS_IMPORT_STILL_RUN })
  );
};

export const starterSyncIsPrepared = (event: Immutable<RacemapEvent | CurrentEvent>): boolean => {
  return (
    event.integrations.genericStarterImport.isActive ||
    (event.integrations.raceresult.isActive && event.integrations.raceresult.contests.length > 0)
  );
};

export const autoImportIsActive = (
  event: Immutable<RacemapEvent | CurrentEvent> | null,
): boolean => {
  if (event == null) return false;
  return !eventIsOver(event) && starterSyncIsPrepared(event);
};

const apiClient = RacemapAPIClient.fromWindowLocation();

export interface RRImportReport extends RaceResultImportCheckResult {
  error: string | null;
  isLoading: boolean;
  sourceUrl: string;
  sourceStatus: 'error' | '' | 'warning' | undefined;
  triggerReload: () => void;
}

export const useRRImport = (event: Immutable<CurrentEvent> | null): RRImportReport => {
  const [sourceStatus, setSourceStatus] = useState<RRImportReport['sourceStatus']>();
  const [contests, setContests] = useState<RRImportReport['contests']>([]);
  const [counts, setCounts] = useState<RRImportReport['counts']>(null);
  const [error, setError] = useState<RRImportReport['error']>(null);
  const [isLoading, setIsLoading] = useState<RRImportReport['isLoading']>(false);
  const [sourceUrl, setSourceUrl] = useState<string>('');

  async function validateRRImport(options: { forceReload?: boolean } = {}) {
    try {
      if (event == null) return;

      const rrImportConf = event.integrations.raceresult;
      const isActivated = rrImportConf.isActive;
      if (!isActivated) return;

      const url = getRaceResultImportUrl(rrImportConf);
      if (url == null) {
        setSourceUrl('');
        setSourceStatus(undefined);
        setContests([]);
        setCounts(null);
        setError(null);
        return;
      }
      if (!options.forceReload && url.href === sourceUrl) return;

      setSourceUrl(url.href);
      setIsLoading(true);
      setSourceStatus(undefined);
      const response = await apiClient.checkRaceresultImport(event.id);
      setContests(response.contests);
      setCounts(response.counts);
      setSourceStatus('');
      setIsLoading(false);
      setError(null);
    } catch (err) {
      if (err instanceof HTTPFetchError) {
        setError(err.message);
        setSourceStatus('error');
        setContests([]);
        setCounts(null);
        setIsLoading(false);
        setSourceUrl('');
      }
    }
  }

  useEffect(() => {
    validateRRImport();
  }, [event]);

  const triggerReload = () => {
    validateRRImport({ forceReload: true });
  };

  return { sourceStatus, contests, counts, error, isLoading, sourceUrl, triggerReload };
};

export const getContestsWithStatus = (
  contests: Immutable<Array<string>>,
  possibleContests: Array<string>,
  sourceStatus: RRImportReport['sourceStatus'],
  isLoading: boolean,
  counts: RRImportReport['counts'],
): Array<Entry> => {
  const combinedContests = Array.from(new Set([...contests, ...possibleContests]))
    .sort((a, b) => sortContests(a, b, counts))
    .filter(isNotEmptyString);

  return combinedContests.map((c) => ({
    value: contests.includes(c),
    switchId: c,
    label: <ContestLabel contestName={c} starterCount={counts?.recordsPerContest[c]} />,
    disabled: isLoading,
    ...getSwitchState(c, contests, possibleContests, sourceStatus),
  }));
};

const sortContests = (a: string, b: string, counts: RRImportReport['counts']): number => {
  if (counts == null) return 0;
  const aCount = counts.recordsPerContest[a] || 0;
  const bCount = counts.recordsPerContest[b] || 0;

  if (aCount === bCount) return a.localeCompare(b);
  return bCount - aCount;
};

const ContestLabel: FC<{ contestName: string; starterCount?: number }> = ({
  contestName,
  starterCount,
}) => (
  <Label>
    <ContestName>{contestName}</ContestName>
    <StarterCount>{starterCount || 0} Starter</StarterCount>
  </Label>
);

const Label = styled.div`
  display: flex;
  align-items: center;
`;
const ContestName = styled.div`
  font-weight: 500;
  min-width: 100px;
`;
const StarterCount = styled.div`
  margin-left: 5px;
  white-space: nowrap;
  flex-shrink: 0;
  color: ${RacemapColors.DarkGray};
`;

const getSwitchState = (
  contestName: string,
  contests: Immutable<Array<string>>,
  possibleContests: Array<string>,
  sourceStatus: RRImportReport['sourceStatus'],
): { switchLabel: string; variant?: 'success' | 'warning' | 'danger'; title?: string } => {
  if (sourceStatus == null && contests.includes(contestName)) {
    return { switchLabel: 'UNKNOWN', variant: 'warning', title: 'Set source url or event id!' };
  }
  if (sourceStatus === 'error') {
    return { switchLabel: 'NO SYNC', variant: 'danger', title: 'Found no valid source file!' };
  }
  if (possibleContests.includes(contestName) && contests.includes(contestName))
    return { switchLabel: 'SYNC', variant: 'success' };
  if (possibleContests.includes(contestName) && !contests.includes(contestName))
    return { switchLabel: 'NO SYNC' };
  return {
    switchLabel: 'UNKNOWN',
    variant: 'warning',
    title: 'Found no starter with that content!',
  };
};
